import { useNotificationModal, useConfirmationModal } from '@room-match/shared-ui-components';
import { useCallback, useState } from 'react';
import { FormModeProps } from 'shared/interfaces/IModalForm';
import { ProgressProps } from './useSubmitForm';

type UseFormModalProps = {
  progress: ProgressProps;
  title: string;
};

type SelectedItemProps = {
  edit: number;
  delete: number;
  view: number;
};

export const useFormModal = ({ progress, title }: UseFormModalProps) => {
  // State to Open the Add Car modal or Edit
  const [openFormModal, setOpenFormModal] = useState(false);
  // State to Open the View Car Modal
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  // Mode of the Modal if Add or Edit
  const [formMode, setFormMode] = useState<FormModeProps>(null);
  // Loading data UI, use when adding a new car

  const [selectedItem, setSelectedItem] = useState<SelectedItemProps>({
    edit: 0,
    delete: 0,
    view: 0,
  });

  // The functionalities of Adding, Editing and Deleting the car

  // Notify Modal
  const { handleSetMessage, handleOpen, NotificationModal, isOpen } = useNotificationModal({
    type: 'warning',
  });
  // A modal functionality when the car is saving or editing or deleting
  const handleNotifyModal = useCallback(() => {
    handleOpen();
    handleSetMessage(`Action in Progress!`, "You can't perform this action for now!", 'Close');
  }, [handleOpen, handleSetMessage]);
  const {
    handleOpen: handleOpenDelete,
    handleSetMessage: handleDeleteMessage,
    isOpen: isModalDeleteOpen,
    ConfirmationModal,
  } = useConfirmationModal({
    type: 'warning',
  });
  // Handling to open the Delete Modal
  const handleDeleteModal = (index: number) => {
    if (progress !== 'idle') return handleNotifyModal();
    handleOpenDelete();
    handleDeleteMessage(`Do you want to delete this ${title}`, 'Do you want to proceed?');
    setSelectedItem((prev) => {
      return { ...prev, delete: index };
    });
  };
  // Handling to open the Form Modal
  const handleOpenFormModal = useCallback(
    (newMode = null, selectedEditedItem?: number) => {
      // If isSaving or isEditing restrict to open the form Modal and open the NotifyModal
      if (progress !== 'idle') {
        handleNotifyModal();
        return;
      }
      // If the mode is on edit, set the selected car
      if (newMode === 'edit' && selectedEditedItem !== undefined) {
        setSelectedItem((prev) => {
          return { ...prev, edit: selectedEditedItem };
        });
      }

      setOpenFormModal((prev) => !prev);
      setFormMode(newMode);
    },
    [handleNotifyModal, progress],
  );

  // Handling to open the View Car Modal
  const handleViewModal = useCallback((index: number) => {
    setOpenViewModal((prev) => !prev);
    setSelectedItem((prev) => {
      return { ...prev, view: index };
    });
  }, []);

  return {
    openFormModal,
    openViewModal,
    formMode,
    selectedItem,
    NotificationModal,
    isOpen,
    ConfirmationModal,
    isModalDeleteOpen,
    handleOpenFormModal,
    handleDeleteModal,
    handleViewModal,
  };
};
